import { apiClient } from './api';
import type {
  WorkAuthResponse,
  DocumentResponse,
  DashboardStats,
  QueryWorkAuth,
  ReviewDocument,
  ApproveWorkAuth,
  SyncStatus,
  ImportResult
} from '@shared/schemas/workAuth';

const BASE_URL = '/work-auth';

export const workAuthService = {
  /**
   * Validate upload token (public - no auth required)
   */
  async validateUploadToken(token: string) {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        renewalCycle: any;
        workAuthorization: WorkAuthResponse;
        employee: any;
      };
    }>(`${BASE_URL}/upload-token/${token}/validate`);
    return response.data as any;
  },

  /**
   * Upload documents using token (public - no auth required)
   */
  async uploadDocumentsByToken(token: string, documentType: string, file: File) {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);

    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: DocumentResponse;
    }>(`${BASE_URL}/upload-token/${token}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Get all work authorizations with filtering
   */
  async getWorkAuthorizations(params: QueryWorkAuth) {
    const response = await apiClient.get<{
      data: WorkAuthResponse[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(BASE_URL, { params });
    return response.data;
  },

  /**
   * Get a single work authorization by ID
   */
  async getWorkAuthorizationById(id: string) {
    const response = await apiClient.get<any>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Get work authorization by employee ID
   */
  async getWorkAuthByEmployeeId(employeeId: string) {
    const response = await apiClient.get<any>(`${BASE_URL}/employee/${employeeId}`);
    return response.data;
  },

  /**
   * Upload work authorization documents
   */
  async uploadDocuments(
    workAuthId: string,
    documentType: string,
    file: File,
    submittedBy: string
  ) {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('workAuthId', workAuthId);
    formData.append('documentType', documentType);
    formData.append('submittedBy', submittedBy);

    const response = await apiClient.post<any>(`${BASE_URL}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Get presigned URL for direct Azure Blob Storage access
   */
  async getDocumentPresignedUrl(documentId: string) {
    // This endpoint requires auth and returns an Azure presigned URL
    const response = await apiClient.get<any>(
      `${BASE_URL}/documents/${documentId}/presigned-url`
    );
    return response.data.url;
  },

  /**
   * Review uploaded documents (HR only)
   */
  async reviewDocument(documentId: string, reviewData: ReviewDocument) {
    const response = await apiClient.post<any>(`${BASE_URL}/documents/${documentId}/review`, reviewData);
    return response.data;
  },

  /**
   * Approve work authorization (set temporary/permanent status)
   */
  async approveWorkAuthorization(workAuthId: string, approvalData: ApproveWorkAuth) {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: WorkAuthResponse;
    }>(`${BASE_URL}/${workAuthId}/approve`, approvalData);
    return response.data;
  },

  /**
   * Get documents requiring review
   */
  async getDocumentsForReview(params?: {
    status?: string;
    page?: number;
    limit?: number;
  }) {
    const response = await apiClient.get<{
      data: any[];
      pagination?: any;
    }>(`${BASE_URL}/documents/pending-review`, { params });
    return response.data;
  },

  /**
   * Get dashboard statistics
   */
  async getDashboardStats() {
    const response = await apiClient.get<{
      success: boolean;
      data: DashboardStats;
    }>(`${BASE_URL}/stats`);
    return response.data;
  },

  /**
   * Terminate work authorization
   */
  async terminateWorkAuth(id: string, reason?: string) {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: WorkAuthResponse;
    }>(`${BASE_URL}/${id}/terminate`, { reason });
    return response.data as any;
  },

  /**
   * Import work authorizations from CSV
   */
  async importFromCSV(file: File) {
    const formData = new FormData();
    formData.append('csv', file);

    const response = await apiClient.post<any>(`${BASE_URL}/import/csv`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Get list of CSV import jobs
   */
  async getImportJobs() {
    const response = await apiClient.get<any[]>(`${BASE_URL}/import/jobs`);
    return response.data;
  },

  /**
   * Get import job status
   */
  async getImportJobStatus(jobId: string) {
    const response = await apiClient.get<any>(`${BASE_URL}/import/job/${jobId}`);
    return response.data;
  },

  /**
   * Export work authorizations to CSV
   */
  async exportToCSV() {
    const response = await apiClient.get<any>(`${BASE_URL}/export/csv`, {
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `work-authorizations-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Manually trigger sync job
   */
  async triggerManualSync() {
    const response = await apiClient.post<any>(`${BASE_URL}/sync/manual`);
    return response.data;
  },

  /**
   * Get sync job status
   */
  async getSyncStatus() {
    const response = await apiClient.get<any>(`${BASE_URL}/sync/status`);
    return response.data;
  },

  /**
   * Manually trigger mailbox polling to check for new document uploads
   */
  async pollMailbox() {
    const response = await apiClient.post<any>(`${BASE_URL}/poll-mailbox`);
    return response.data;
  },

  /**
   * Get Smartsheet preview data with pagination
   */
  async getSmartsheetPreview(page: number = 1, pageSize: number = 50) {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        sheetId: string;
        sheetName: string;
        totalRows: number;
        totalColumns: number;
        columns: Array<{
          id: number;
          title: string;
          type: string;
          index: number;
          primary?: boolean;
        }>;
        rows: Array<{
          id: number;
          rowNumber: number;
          data: Record<string, any>;
        }>;
        pagination: {
          page: number;
          pageSize: number;
          totalPages: number;
          hasMore: boolean;
        };
      };
    }>(`${BASE_URL}/smartsheet/preview`, {
      params: { page, pageSize }
    });
    return response.data;
  },

  /**
   * Get Smartsheet sync status
   */
  async getSmartsheetSyncStatus() {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        isScheduled: boolean;
        isRunning: boolean;
        lastRun: string;
        nextRun: string;
        timezone: string;
        syncEnabled: boolean;
        syncSheetId: string | null;
        lastResult: {
          success: boolean;
          timestamp: string;
          totalRowsInSheet: number;
          newEmployeesCreated: number;
          employeesUpdated: number;
          workAuthsCreated: number;
          workAuthsUpdated: number;
          skipped: number;
          renewalCyclesCreated: number;
          errors: Array<{ rowNumber: number; employeeId: string; error: string }>;
          durationMs: number;
        } | null;
      };
    }>(`${BASE_URL}/smartsheet/sync/status`);
    return response.data;
  },

  /**
   * Manually trigger Smartsheet sync
   */
  async triggerSmartsheetSync() {
    const response = await apiClient.post<{
      success: boolean;
      data: {
        success: boolean;
        timestamp: string;
        totalRowsInSheet: number;
        newEmployeesCreated: number;
        employeesUpdated: number;
        workAuthsCreated: number;
        workAuthsUpdated: number;
        skipped: number;
        renewalCyclesCreated: number;
        errors: Array<{ rowNumber: number; employeeId: string; error: string }>;
        durationMs: number;
      };
    }>(`${BASE_URL}/smartsheet/sync/trigger`);
    return response.data;
  },

  /**
   * Get Smartsheet audit logs (writes to Smartsheet)
   */
  async getSmartsheetAuditLogs(page: number = 1, limit: number = 50) {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        logs: Array<{
          id: string;
          workAuthId: string;
          action: string;
          columnName: string;
          oldValue: string | null;
          newValue: string;
          smartsheetRowId: string;
          performedBy: string;
          success: boolean;
          errorMessage: string | null;
          createdAt: string;
          performer: { id: string; email: string; name: string } | null;
          workAuth: {
            id: string;
            employee: { employeeId: string; firstName: string; lastName: string };
          } | null;
        }>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      };
    }>(`${BASE_URL}/smartsheet/audit-logs`, { params: { page, limit } });
    return response.data;
  },

  /**
   * Get the Smartsheet record for a specific work authorization
   */
  async getSmartsheetRecord(workAuthId: string) {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        sheetName: string;
        rowId: number;
        rowNumber: number;
        columns: string[];
        record: Record<string, any>;
      } | null;
      message?: string;
    }>(`${BASE_URL}/${workAuthId}/smartsheet-record`);
    return response.data;
  },

  /**
   * Get employees requiring renewal
   */
  async getEmployeesRequiringRenewal(daysUntilExpiration: number = 90) {
    const response = await apiClient.get<any>(`${BASE_URL}/employees-requiring-renewal`, {
      params: { days: daysUntilExpiration }
    });
    return response.data;
  },

  /**
   * Testing Methods
   */

  /**
   * List all test work authorization data
   */
  async listTestData() {
    const response = await apiClient.get<{
      success: boolean;
      data: Array<{
        employeeId: string;
        id: string;
        name: string;
        email: string;
        managerEmail: string;
        workAuth: {
          id: string;
          expirationDate: string;
          daysUntilExpiration: number;
          currentStatus: string;
          notificationCount: number;
          lastNotification: {
            notificationType: string;
            recipientEmail: string;
            sentAt: string;
          } | null;
          uploadToken: string | null;
        };
      }>;
    }>(`${BASE_URL}/test-data/list`);
    // Backend returns: { success, data: [...] }
    return response.data;
  },

  /**
   * Trigger manual notifications
   */
  async triggerManualNotifications(params?: {
    workAuthId?: string;
    employeeId?: string;
    forceResend?: boolean;
  }) {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: {
        processed: number;
        sent: number;
        failed: number;
        employee?: any;
        error?: string;
      };
    }>(`${BASE_URL}/notifications/trigger-manual`, params || {});
    return response.data as any;
  },

  /**
   * Get notification history for a work authorization
   */
  async getNotificationHistory(workAuthId: string) {
    const response = await apiClient.get<{
      success: boolean;
      data: Array<{
        id: string;
        notificationType: string;
        recipientEmail: string;
        sentAt: string;
      }>;
    }>(`${BASE_URL}/${workAuthId}/notification-history`);
    return response.data as any;
  },

  /**
   * Clean up all test data
   */
  async cleanupTestData() {
    const response = await apiClient.delete<{
      success: boolean;
      message: string;
      data: {
        deletedNotifications: number;
        deletedDocuments: number;
        deletedCycles: number;
        deletedWorkAuths: number;
        deletedEmployees: number;
      };
    }>(`${BASE_URL}/test-data/cleanup`);
    return response.data as any; // Unwrap data
  },

  /**
   * Get incoming emails with pagination
   */
  async getIncomingEmails(page: number = 1, limit: number = 20) {
    const response = await apiClient.get<{
      success: boolean;
      data: Array<{
        id: string;
        messageId: string;
        subject: string;
        from: string;
        to: string[];
        body: string;
        htmlBody: string | null;
        receivedAt: string;
        processed: boolean;
        processedAt: string | null;
        error: string | null;
        relatedWorkAuthId: string | null;
        createdAt: string;
        workAuthorization: {
          id: string;
          employee: {
            firstName: string;
            lastName: string;
            email: string;
          };
        } | null;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(`${BASE_URL}/incoming-emails`, {
      params: { page, limit }
    });
    return response.data;
  },

  /**
   * Get a single incoming email by ID
   */
  async getIncomingEmailById(id: string) {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        id: string;
        messageId: string;
        subject: string;
        from: string;
        to: string[];
        body: string;
        htmlBody: string | null;
        receivedAt: string;
        processed: boolean;
        processedAt: string | null;
        error: string | null;
        relatedWorkAuthId: string | null;
        createdAt: string;
        workAuthorization: {
          id: string;
          employee: {
            firstName: string;
            lastName: string;
            email: string;
          };
        } | null;
      };
    }>(`${BASE_URL}/incoming-emails/${id}`);
    return response.data;
  },

  /**
   * Backup Methods
   */

  /**
   * List all Smartsheet backups
   */
  async listBackups() {
    const response = await apiClient.get<{
      success: boolean;
      data: Array<{
        filename: string;
        date: string;
        path: string;
      }>;
    }>(`${BASE_URL}/backups`);
    return response.data;
  },

  /**
   * Get backup scheduler status
   */
  async getBackupStatus() {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        isScheduled: boolean;
        isRunning: boolean;
        lastRun: string | null;
        nextRun: string | null;
        timezone: string;
        lastBackupResult: {
          success: boolean;
          filename: string;
          timestamp: string;
          rowCount: number;
          sizeBytes: number;
          error?: string;
        } | null;
      };
    }>(`${BASE_URL}/backups/status`);
    return response.data;
  },

  /**
   * Manually trigger a backup
   */
  async triggerBackup() {
    const response = await apiClient.post<{
      success: boolean;
      data: {
        success: boolean;
        filename: string;
        timestamp: string;
        rowCount: number;
        sizeBytes: number;
        error?: string;
      };
    }>(`${BASE_URL}/backups/trigger`);
    return response.data;
  },

  /**
   * Get presigned download URL for a backup file
   */
  async getBackupDownloadUrl(filename: string) {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        url: string;
        filename: string;
        expiresIn: number;
      };
    }>(`${BASE_URL}/backups/${filename}`);
    return response.data;
  }
};